/**
 * Admin Scenarios — list + create/edit/delete OSCE cases (admin-authored).
 * Stored separately in db.admin_scenarios so it doesn't collide with the seeded
 * 33 osce_learn_content scenarios. v1: simple metadata + a single "sections JSON" editor.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { api } from "@/App";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const blank = {
  title: "",
  subject_id: "oral_medicine",
  estimated_time: 15,
  sections: [],
  key_takeaways: [],
  exam_tips: [],
};

const AdminScenariosPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [subjects, setSubjects] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get("/admin/scenarios");
      setItems(d.scenarios);
    } catch { toast.error("Could not load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); api.get("/subjects").then((d) => setSubjects(d.subjects)); }, [load]);

  const handleDelete = async (sc) => {
    if (!window.confirm(`Delete scenario "${sc.title}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/scenarios/${sc.id}`, { withCredentials: true });
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-scenarios-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">OSCE Scenarios</h2>
          <p className="text-sm text-muted-foreground">{items.length} admin-authored cases. Built-in 33 seeded cases live separately.</p>
        </div>
        <button data-testid="admin-create-scenario-btn" onClick={() => setEditing(blank)} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium">
          <Plus className="w-4 h-4" /> New scenario
        </button>
      </div>

      <div className="border border-border rounded-2xl overflow-hidden bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Title</th>
              <th className="px-4 py-3 font-medium">Subject</th>
              <th className="px-4 py-3 font-medium">Sections</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">None yet — create your first scenario.</td></tr>}
            {items.map((sc) => (
              <tr key={sc.id} className="border-t border-border">
                <td className="px-4 py-3">{sc.title}</td>
                <td className="px-4 py-3 text-muted-foreground">{sc.subject_id}</td>
                <td className="px-4 py-3 text-muted-foreground">{sc.sections?.length || 0}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setEditing(sc)} className="p-1.5 rounded hover:bg-secondary"><Edit2 className="w-4 h-4 text-muted-foreground" /></button>
                    <button onClick={() => handleDelete(sc)} className="p-1.5 rounded hover:bg-destructive/15"><Trash2 className="w-4 h-4 text-destructive" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <ScenarioEditModal scenario={editing} subjects={subjects} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const ScenarioEditModal = ({ scenario, subjects, onClose, onSaved }) => {
  const isNew = !scenario.id;
  const [form, setForm] = useState({
    ...scenario,
    sections_json: JSON.stringify(scenario.sections || [], null, 2),
    key_takeaways_str: (scenario.key_takeaways || []).join("\n"),
    exam_tips_str: (scenario.exam_tips || []).join("\n"),
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let sections;
      try { sections = JSON.parse(form.sections_json || "[]"); }
      catch { toast.error("Sections JSON is invalid"); setSaving(false); return; }

      const payload = {
        title: form.title,
        subject_id: form.subject_id,
        estimated_time: Number(form.estimated_time) || 15,
        sections,
        key_takeaways: form.key_takeaways_str.split("\n").map((s) => s.trim()).filter(Boolean),
        exam_tips: form.exam_tips_str.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (isNew) await api.post("/admin/scenarios", payload);
      else await axios.patch(`${BACKEND_URL}/api/admin/scenarios/${scenario.id}`, payload, { withCredentials: true });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="scenario-edit-modal">
      <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-semibold">{isNew ? "New scenario" : "Edit scenario"}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-secondary"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-3">
          <input data-testid="sc-title" required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="sc-subject" value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} className="px-3 py-2 rounded-lg bg-background border border-border text-sm">
              {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input data-testid="sc-time" type="number" placeholder="Estimated minutes" value={form.estimated_time} onChange={(e) => setForm({ ...form, estimated_time: e.target.value })} className="px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Sections (JSON array of {`{ title, content, key_facts? }`})</label>
            <textarea data-testid="sc-sections" rows={10} value={form.sections_json} onChange={(e) => setForm({ ...form, sections_json: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-xs font-mono" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Key takeaways (one per line)</label>
            <textarea data-testid="sc-takeaways" rows={3} value={form.key_takeaways_str} onChange={(e) => setForm({ ...form, key_takeaways_str: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Exam tips (one per line)</label>
            <textarea data-testid="sc-tips" rows={3} value={form.exam_tips_str} onChange={(e) => setForm({ ...form, exam_tips_str: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          </div>
          <div className="flex gap-2 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 rounded-lg border border-border text-sm">Cancel</button>
            <button type="submit" data-testid="sc-save" disabled={saving} className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-50">
              {saving ? "Saving…" : (isNew ? "Create" : "Save changes")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminScenariosPage;
